import { CalendarApi, EventInput } from '@fullcalendar/core';
import { DateOverrideDto, WorkingTimeResponse } from '../model/fss.api';

/** Minutes since midnight; `to` may be 1440 (end of day). */
export interface MinuteRange {
  from: number;
  to: number;
}

const DAY_MINUTES = 24 * 60;

/** "09:00:00" → 540. */
function parseTime(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const [h, m] = value.split(':').map(Number);
  return Number.isFinite(h) ? h * 60 + (m || 0) : undefined;
}

/** Working hours plus every date-override interval (an override can open earlier or close later). */
export function workingHoursRanges(
  workingTime: WorkingTimeResponse | undefined,
  overrides: DateOverrideDto[] | undefined,
): MinuteRange[] {
  const intervals = [
    { from: workingTime?.from, to: workingTime?.to },
    ...(overrides ?? []).flatMap((o) => o.intervals ?? []),
  ];
  return intervals.flatMap((i) => {
    const from = parseTime(i.from);
    const to = parseTime(i.to);
    if (from === undefined || to === undefined) return [];
    // "00:00" as the end means midnight.
    return [{ from, to: to <= from ? DAY_MINUTES : to }];
  });
}

/** Time-of-day span of each event; one that runs past midnight reaches the end of the day. */
export function eventRanges(events: EventInput[]): MinuteRange[] {
  return events.flatMap((e) => {
    if (!(e.start instanceof Date) || !(e.end instanceof Date)) return [];
    const from = e.start.getHours() * 60 + e.start.getMinutes();
    const sameDay = e.start.toDateString() === e.end.toDateString();
    const to = sameDay ? e.end.getHours() * 60 + e.end.getMinutes() : DAY_MINUTES;
    return [{ from, to }];
  });
}

function toSlotTime(minutes: number): string {
  return `${String(minutes / 60).padStart(2, '0')}:00:00`;
}

/**
 * Fits the grid to cover every range, rounded out to whole hours
 * (09:00–23:15 → 09:00–24:00). Pass the working hours together with the loaded
 * events, so an appointment outside the configured hours (e.g. booked before
 * they changed) is never cut off. Returns the window now shown.
 */
export function fitSlotTimes(
  api: CalendarApi | undefined,
  current: MinuteRange,
  ranges: MinuteRange[],
): MinuteRange {
  if (!api || ranges.length === 0) return current;
  const min = Math.floor(Math.min(...ranges.map((r) => r.from)) / 60) * 60;
  const max = Math.ceil(Math.max(...ranges.map((r) => r.to)) / 60) * 60;
  const next = { from: min, to: max };
  if (next.from !== current.from) api.setOption('slotMinTime', toSlotTime(next.from));
  if (next.to !== current.to) api.setOption('slotMaxTime', toSlotTime(next.to));
  return next;
}

/** Slot options for a window, for the initial calendar options. */
export function slotTimes(range: MinuteRange): { slotMinTime: string; slotMaxTime: string } {
  return { slotMinTime: toSlotTime(range.from), slotMaxTime: toSlotTime(range.to) };
}

/** Whole local days around a range, so events outside the visible hours are still fetched. */
export function wholeDays(start: Date, end: Date): { start: Date; end: Date } {
  const s = new Date(start);
  s.setHours(0, 0, 0, 0);
  const e = new Date(end);
  if (e.getHours() || e.getMinutes() || e.getSeconds() || e.getMilliseconds()) {
    e.setHours(24, 0, 0, 0);
  }
  return { start: s, end: e };
}
